"use client";

import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { Menu, LogOut, LogIn, Sun, Moon } from "lucide-react";
import { useEffect, useState } from "react";
import MiniDNA from "@/components/MiniDna";

export default function Header() {
  const router = useRouter();
  const pathname = usePathname();
  const isDashboard = pathname.startsWith("/dashboard");

  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // auth state (hydrate-safe)
  const [isAuthed, setIsAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsAuthed(!!token);
  }, []);

  // scroll detection (حل مشکل افتادن متن)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("compubio_logged_in");
    document.cookie = "token=; path=/; max-age=0; SameSite=Lax";
    router.push("/login");
  };

  return (
    <header
      className={` fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-xl
        ${
          dark
            ? scrolled
              ? "bg-[#020617]/95 border-b border-indigo-500/30 shadow-xl text-slate-200"
              : "bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.15),transparent_45%),linear-gradient(to_bottom,#020617,#020617)] border-b border-indigo-500/20 text-slate-200"
            : scrolled
            ? "bg-white/95 border-b border-indigo-300/40 shadow-lg text-slate-800"
            : "bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.15),transparent_45%),linear-gradient(to_bottom,#f8fafc,#eef2ff)] border-b border-indigo-300/40 text-slate-800"
        }
      `}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2 group">
          <MiniDNA />
          <div className="leading-tight">
            <span
              className={`
                block text-[22px] font-extrabold tracking-tight
                font-[var(--font-title)]
                bg-gradient-to-r
                ${
                  dark
                    ? "from-cyan-300 via-indigo-300 to-purple-300"
                    : "from-cyan-500 via-indigo-500 to-purple-500"
                }
                bg-clip-text text-transparent
              `}
            >
              CompuBio
            </span>
            <span
              className={`text-[10px] ${
                dark ? "text-indigo-300/60" : "text-indigo-600/70"
              }`}
            >
              Genome Intelligence
            </span>
          </div>
        </Link>

        {/* Desktop navigation */}
        <nav
          className={`
            hidden md:flex items-center gap-6 text-sm font-semibold
            ${dark ? "text-indigo-200/80" : "text-indigo-700"}
          `}
        >
          {[
            { href: "/dashboard", label: "Dashboard" },
            { href: "/pricing", label: "Pricing" },
            { href: "/docs", label: "Services" },
            { href: "/about", label: "About" },
          ].map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className=" relative transition hover:text-cyan-400 after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:bg-cyan-400 after:transition-all hover:after:w-full ">
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Right actions */}
        <div className="hidden md:flex items-center gap-3">
          {/* Theme toggle */}
          <button
            onClick={() => setDark(!dark)}
            className={`
              p-2 rounded-full transition
              ${dark ? "hover:bg-white/10" : "hover:bg-indigo-100"}
            `}
            aria-label="Toggle theme"
          >
            {dark ? <Moon size={18} /> : <Sun size={18} />}
          </button>

          {isAuthed ? (
            <button
              onClick={logout}
              className={` inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-semibold transition
                ${
                  dark
                    ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                    : "bg-red-100 text-red-600 hover:bg-red-200"
                }
              `}
            >
              <LogOut size={16} />
              Logout
            </button>
          ) : (
            <Link
              href="/login"
              className={`
                inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5
                text-sm font-semibold transition
                ${
                  dark
                    ? "bg-indigo-500/30 text-indigo-200 hover:bg-indigo-500/40"
                    : "bg-indigo-600 text-white hover:bg-indigo-700"
                }
              `}
            >
              <LogIn size={16} />
              Login
            </Link>
          )}
        </div>

        {/* Mobile menu button */}
        <button
          className={`
            md:hidden inline-flex items-center justify-center rounded-xl p-2
            ${dark ? "hover:bg-white/10" : "hover:bg-indigo-100"}
          `}
          onClick={() => setOpen((v) => !v)}
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div
          className={`
            md:hidden px-6 py-4 space-y-4
            ${
              dark
                ? "bg-[#020617] border-t border-indigo-500/20"
                : "bg-white border-t border-indigo-200"
            }
          `}
        >
          {[
            { href: "/dashboard", label: "Dashboard" },
            { href: "/pricing", label: "Pricing" },
            { href: "/docs", label: "Services" },
            { href: "/about", label: "About" },
          ].map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={`block text-sm font-semibold ${
                dark ? "text-indigo-200 hover:text-cyan-400" : "text-indigo-700"
              }`}
            >
              {item.label}
            </Link>
          ))}

          <div className="pt-3 border-t border-indigo-500/20 flex items-center gap-3">
            <button
              onClick={() => setDark(!dark)}
              className="inline-flex items-center gap-2 text-sm font-semibold"
            >
              {dark ? <Moon size={16} /> : <Sun size={16} />}
              Theme
            </button>

            {isAuthed ? (
              <button
                onClick={logout}
                className="inline-flex items-center gap-2 text-sm font-semibold text-red-500"
              >
                <LogOut size={16} />
                Logout
              </button>
            ) : (
              <Link
                href="/login"
                className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600"
              >
                <LogIn size={16} />
                Login
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
